// ============================================================
// components/UI.jsx
// Componentes UI reutilizables: inputs, selects, cards, badges
// Estilo: cyberpunk industrial con neón naranja/cyan
// ============================================================

import React from 'react';

/* ---- Estilos compartidos como objetos JavaScript ---- */

const inputBaseStyle = {
  width: '100%',
  background: '#111120',                  /* Un poco más claro que bg-input puro */
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  color: '#F0F0FA',                        /* Texto muy legible */
  fontFamily: 'var(--font-body)',
  fontSize: '1rem',
  fontWeight: '500',
  padding: '10px 14px',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  fontFamily: 'var(--font-body)',
  fontWeight: '700',
  fontSize: '0.8rem',
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: '#C8C8E0',                        /* Gris claro legible */
  marginBottom: '6px',
};

const errorStyle = {
  color: '#FF6666',
  fontSize: '0.75rem',
  marginTop: '4px',
  fontFamily: 'var(--font-mono)',
};

const hintStyle = {
  color: '#9090B8',                        /* Gris medio, readable */
  fontSize: '0.73rem',
  marginTop: '4px',
  fontFamily: 'var(--font-mono)',
};

// ---- Componente: Campo de texto/número con validación ----
export function FormField({
  label,
  id,
  type = 'text',
  value,
  onChange,
  error,
  hint,
  placeholder,
  prefix,
  suffix,
  min,
  max,
  step,
  required,
}) {
  const [focused, setFocused] = React.useState(false);

  const wrapperStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const prefixStyle = {
    position: 'absolute',
    left: '12px',
    color: 'var(--neon-cyan)',
    fontFamily: 'var(--font-mono)',
    fontWeight: '700',
    fontSize: '0.9rem',
    pointerEvents: 'none',
    zIndex: 1,
  };

  const suffixStyle = {
    position: 'absolute',
    right: '12px',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-mono)',
    fontSize: '0.85rem',
    pointerEvents: 'none',
  };

  const fieldStyle = {
    ...inputBaseStyle,
    paddingLeft:  prefix ? '30px' : '14px',
    paddingRight: suffix ? '50px' : '14px',
    borderColor: error
      ? '#FF4444'
      : focused
        ? 'var(--neon-cyan)'
        : 'var(--border-subtle)',
    boxShadow: focused
      ? '0 0 0 2px rgba(0, 212, 255, 0.15)'
      : 'none',
  };

  return (
    <div style={{ marginBottom: '4px' }}>
      {label && (
        <label htmlFor={id} style={labelStyle}>
          {label}
          {required && (
            <span style={{ color: 'var(--neon-orange)', marginLeft: '4px' }}>*</span>
          )}
        </label>
      )}
      <div style={wrapperStyle}>
        {prefix && <span style={prefixStyle}>{prefix}</span>}
        <input
          id={id}
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          min={min}
          max={max}
          step={step}
          style={fieldStyle}
        />
        {suffix && <span style={suffixStyle}>{suffix}</span>}
      </div>
      {error && <p style={errorStyle}>{error}</p>}
      {hint && !error && <p style={hintStyle}>{hint}</p>}
    </div>
  );
}

// ---- Componente: Select con estilo ----
export function FormSelect({ label, id, value, onChange, options, error, hint }) {
  const [focused, setFocused] = React.useState(false);

  const selectStyle = {
    ...inputBaseStyle,
    cursor: 'pointer',
    paddingRight: '36px',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%2300D4FF' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    borderColor: error
      ? '#FF4444'
      : focused
        ? 'var(--neon-cyan)'
        : 'var(--border-subtle)',
    boxShadow: focused
      ? '0 0 0 2px rgba(0, 212, 255, 0.15)'
      : 'none',
  };

  return (
    <div style={{ marginBottom: '4px' }}>
      {label && (
        <label htmlFor={id} style={labelStyle}>
          {label}
        </label>
      )}
      <select
        id={id}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={selectStyle}
      >
        {options.map(opt => (
          <option
            key={opt.value}
            value={opt.value}
            style={{
              background: '#1A1A2E',
              color: '#F0F0FA',
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '0.95rem',
              padding: '8px',
            }}
          >
            {opt.label}
          </option>
        ))}
      </select>
      {error && <p style={errorStyle}>{error}</p>}
      {hint && !error && <p style={hintStyle}>{hint}</p>}
    </div>
  );
}

// ---- Componente: Textarea con estilo ----
export function FormTextarea({ label, id, value, onChange, rows = 6, hint }) {
  const [focused, setFocused] = React.useState(false);

  return (
    <div style={{ marginBottom: '4px' }}>
      {label && (
        <label htmlFor={id} style={labelStyle}>
          {label}
        </label>
      )}
      <textarea
        id={id}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        rows={rows}
        style={{
          ...inputBaseStyle,
          resize: 'vertical',
          lineHeight: '1.6',
          borderColor: focused ? 'var(--neon-cyan)' : 'var(--border-subtle)',
          boxShadow: focused ? '0 0 0 2px rgba(0, 212, 255, 0.15)' : 'none',
        }}
      />
      {hint && <p style={hintStyle}>{hint}</p>}
    </div>
  );
}

// ---- Componente: Tarjeta de sección del formulario ----
export function SectionCard({ title, icon, accentColor = 'var(--neon-cyan)', children, subtitle }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: `1px solid ${accentColor}33`,
      borderLeft: `3px solid ${accentColor}`,
      borderRadius: 'var(--radius-lg)',
      padding: '24px',
      marginBottom: '20px',
      position: 'relative',
      overflow: 'hidden',
      animation: 'fadeInUp 0.4s ease both',
    }}>
      {/* Glow decorativo en la esquina superior ---- */}
      <div style={{
        position: 'absolute',
        top: '-20px',
        right: '-20px',
        width: '80px',
        height: '80px',
        background: `radial-gradient(circle, ${accentColor}20, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      <div style={{ marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {icon && (
            <span style={{
              fontSize: '1.2rem',
              background: `${accentColor}20`,
              padding: '6px 8px',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${accentColor}40`,
            }}>
              {icon}
            </span>
          )}
          <h3 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '0.85rem',
            fontWeight: '700',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: accentColor,
            margin: 0,
          }}>
            {title}
          </h3>
        </div>
        {subtitle && (
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '0.8rem',
            marginTop: '6px',
            marginLeft: icon ? '42px' : '0',
            fontFamily: 'var(--font-body)',
          }}>
            {subtitle}
          </p>
        )}
      </div>

      {/* Grid de contenido ---- */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '16px',
      }}>
        {children}
      </div>
    </div>
  );
}

// ---- Componente: Celda de resultado del cálculo ----
export function ResultCell({ label, value, accent = false, large = false, formula }) {
  return (
    <div style={{
      background: accent ? 'rgba(255, 107, 0, 0.08)' : 'rgba(0, 212, 255, 0.05)',
      border: `1px solid ${accent ? 'rgba(255, 107, 0, 0.3)' : 'rgba(0, 212, 255, 0.2)'}`,
      borderRadius: 'var(--radius-md)',
      padding: large ? '20px' : '14px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    }}>
      <span style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.72rem',
        fontWeight: '600',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--text-secondary)',
      }}>
        {label}
      </span>
      <span style={{
        fontFamily: 'var(--font-mono)',
        fontSize: large ? '1.6rem' : '1.1rem',
        fontWeight: '700',
        color: accent ? 'var(--neon-orange)' : 'var(--neon-cyan)',
        textShadow: accent ? 'var(--glow-orange)' : 'var(--glow-cyan)',
        letterSpacing: large ? '0.05em' : 'normal',
      }}>
        {value}
      </span>
      {formula && (
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.65rem',
          color: 'rgba(136, 136, 168, 0.7)',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          paddingTop: '4px',
        }}>
          {formula}
        </span>
      )}
    </div>
  );
}

// ---- Componente: Botón principal con efecto neón ----
export function NeonButton({
  children,
  onClick,
  variant = 'primary',
  fullWidth = false,
  disabled = false,
  icon,
}) {
  const [hovered, setHovered] = React.useState(false);

  const variants = {
    primary: {
      background: hovered
        ? 'linear-gradient(135deg, #FF8C00, #FF6B00)'
        : 'linear-gradient(135deg, #FF6B00, #CC4400)',
      border: '1px solid rgba(255, 107, 0, 0.6)',
      color: '#FFFFFF',
      boxShadow: hovered ? 'var(--glow-orange)' : 'none',
    },
    secondary: {
      background: hovered
        ? 'rgba(0, 212, 255, 0.15)'
        : 'rgba(0, 212, 255, 0.05)',
      border: '1px solid rgba(0, 212, 255, 0.5)',
      color: 'var(--neon-cyan)',
      boxShadow: hovered ? 'var(--glow-cyan)' : 'none',
    },
    success: {
      background: hovered
        ? 'rgba(57, 255, 20, 0.2)'
        : 'rgba(57, 255, 20, 0.1)',
      border: '1px solid rgba(57, 255, 20, 0.5)',
      color: 'var(--neon-green)',
      boxShadow: hovered ? 'var(--glow-green)' : 'none',
    },
    danger: {
      background: hovered
        ? 'rgba(255, 68, 68, 0.2)'
        : 'rgba(255, 68, 68, 0.05)',
      border: '1px solid rgba(255, 68, 68, 0.4)',
      color: '#FF4444',
      boxShadow: 'none',
    },
  };

  const style = variants[variant] || variants.primary;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: fullWidth ? '100%' : 'auto',
        padding: '12px 24px',
        borderRadius: 'var(--radius-md)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'var(--font-display)',
        fontSize: '0.78rem',
        fontWeight: '700',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        transition: 'all 0.2s ease',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {icon && <span>{icon}</span>}
      {children}
    </button>
  );
}

// ---- Componente: Badge de estado ----
export function Badge({ children, color = 'cyan' }) {
  const colors = {
    cyan:   { bg: 'rgba(0, 212, 255, 0.15)',  border: 'rgba(0, 212, 255, 0.4)',  text: 'var(--neon-cyan)' },
    orange: { bg: 'rgba(255, 107, 0, 0.15)',  border: 'rgba(255, 107, 0, 0.4)',  text: 'var(--neon-orange)' },
    green:  { bg: 'rgba(57, 255, 20, 0.12)',  border: 'rgba(57, 255, 20, 0.4)',  text: 'var(--neon-green)' },
    pink:   { bg: 'rgba(255, 0, 122, 0.12)',  border: 'rgba(255, 0, 122, 0.4)',  text: 'var(--neon-pink)' },
    yellow: { bg: 'rgba(255, 229, 0, 0.12)',  border: 'rgba(255, 229, 0, 0.4)',  text: 'var(--neon-yellow)' },
  };

  const c = colors[color] || colors.cyan;

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 10px',
      background: c.bg,
      border: `1px solid ${c.border}`,
      borderRadius: '100px',
      color: c.text,
      fontFamily: 'var(--font-mono)',
      fontSize: '0.7rem',
      fontWeight: '600',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    }}>
      {children}
    </span>
  );
}

// ---- Componente: Divisor decorativo ----
export function NeonDivider({ color = 'var(--neon-cyan)' }) {
  return (
    <div style={{
      height: '1px',
      background: `linear-gradient(90deg, transparent, ${color}60, ${color}60, transparent)`,
      margin: '8px 0',
    }} />
  );
}

// ---- Componente: Info de fórmula ----
export function FormulaDisplay({ formula, description }) {
  return (
    <div style={{
      background: 'rgba(0, 0, 0, 0.3)',
      border: '1px solid rgba(255, 229, 0, 0.2)',
      borderRadius: 'var(--radius-sm)',
      padding: '8px 12px',
      fontFamily: 'var(--font-mono)',
      fontSize: '0.72rem',
      color: 'var(--neon-yellow)',
      letterSpacing: '0.03em',
    }}>
      <span style={{ color: 'var(--text-secondary)', marginRight: '8px' }}>∑</span>
      {formula}
      {description && (
        <span style={{
          display: 'block',
          color: 'var(--text-secondary)',
          fontSize: '0.65rem',
          marginTop: '2px',
          fontFamily: 'var(--font-body)',
          letterSpacing: 'normal',
        }}>
          {description}
        </span>
      )}
    </div>
  );
}
